import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import PageLoader from '../Components/PageLoader';
import Table from '../Components/Tables';
import {
  SimulationDatasetApiProvider,
  useSimulationDatasetApi,
} from '../Services/SimulationDatasetApiProvider';
import { EvalRunDetail, EvalRunTest } from '../Services/SimulationDatasetApiProviderTypes';
import { formatDateTimeLocal } from '../utils/formatDate';
import './SimulationDatasetConversation.css';

function StatBox({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="sim-stat-box">
      <div className="sim-stat-value">{value}</div>
      <div className="sim-stat-label">{label}</div>
    </div>
  );
}

function testStatusLabel(test: EvalRunTest): string {
  const scan = test.scan;
  if (scan.status === 'ERROR') return 'Error';
  if (scan.status !== 'DONE') return scan.status;
  return `${scan.passed ? 'Passed' : 'Failed'}${scan.score !== null ? ` (${scan.score})` : ''}`;
}

function SimulationDatasetEvaluationDetailContent() {
  const { organizationId, datasetKey, runKey } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { getEvaluationDetail, getEvaluationScans } = useSimulationDatasetApi();

  const [loading, setLoading] = useState<boolean>(true);
  const [detail, setDetail] = useState<EvalRunDetail | null>(null);
  const [tests, setTests] = useState<EvalRunTest[]>([]);
  const [testsTotal, setTestsTotal] = useState<number>(0);
  const [testsLoading, setTestsLoading] = useState<boolean>(false);
  const [skip, setSkip] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);

  const loadDetail = useCallback(
    async (showSpinner = true): Promise<void> => {
      if (!datasetKey || !runKey) return;
      try {
        if (showSpinner) setLoading(true);
        const data = await getEvaluationDetail(datasetKey, runKey);
        setDetail(data);
      } catch (error) {
        console.error('Error loading evaluation:', error);
        showAlert('fail', 'Failed to load evaluation');
      } finally {
        setLoading(false);
      }
    },
    [datasetKey, runKey],
  );

  const loadTests = useCallback(
    async (pageSkip: number, pageLimit: number): Promise<void> => {
      if (!datasetKey || !runKey) return;
      try {
        setTestsLoading(true);
        const data = await getEvaluationScans(datasetKey, runKey, pageSkip, pageLimit);
        setTests(data.items);
        setTestsTotal(data.totalCount);
      } catch (error) {
        console.error('Error loading evaluation scans:', error);
        showAlert('fail', 'Failed to load tests');
      } finally {
        setTestsLoading(false);
      }
    },
    [datasetKey, runKey],
  );

  const onChangePage = (pageSkip: number, pageLimit: number): void => {
    setSkip(pageSkip);
    if (pageLimit !== perPage) setPerPage(pageLimit);
    loadTests(pageSkip, pageLimit);
  };

  useEffect(() => {
    loadDetail();
    loadTests(0, perPage);
  }, [datasetKey, runKey]);

  useEffect(() => {
    if (!detail || detail.status !== 'RUNNING') return undefined;
    const t = setInterval(() => {
      loadDetail(false);
      loadTests(skip, perPage);
    }, 4000);
    return () => clearInterval(t);
  }, [detail?.status, skip, perPage]);

  const onBack = (): void => {
    navigate(`/organizations/${organizationId}/simulation-datasets/${datasetKey}`);
  };

  const openTest = (test: EvalRunTest): void => {
    navigate(
      `/organizations/${organizationId}/simulation-datasets/${datasetKey}/evaluations/${runKey}/tests/${test.scan.key}`,
    );
  };

  if (loading) return <PageLoader />;
  if (!detail) return null;

  const columns = [
    {
      name: 'Name',
      selector: (row: EvalRunTest) => row.conversation_name ?? 'Untitled test',
      sortable: false,
    },
    {
      name: 'Status',
      selector: (row: EvalRunTest) => testStatusLabel(row),
      sortable: false,
    },
  ];

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <div className="d-flex justify-between align-center" style={{ marginBottom: 12 }}>
            <h2 className="h2-title" style={{ margin: 0 }}>
              Evaluation
            </h2>
            <Button variant="ghost" size="medium" onClick={onBack}>
              Back
            </Button>
          </div>

          <div className="gap d-flex flex-column">
            <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Overview</h4>
              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Agent</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <p>{detail.assistant_name || '—'}</p>
                </div>
              </div>
              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Created At</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <p>{detail.started_at ? formatDateTimeLocal(detail.started_at) : '—'}</p>
                </div>
              </div>
              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Status</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <p>{detail.status === 'RUNNING' ? 'Running…' : 'Done'}</p>
                </div>
              </div>

              <div className="sim-stats">
                <StatBox label="Tests" value={detail.total} />
                <StatBox label="Passed" value={detail.passed} />
                <StatBox label="Failed" value={detail.failed} />
                <StatBox label="Avg score" value={detail.avg_score} />
              </div>
            </div>

            <div className="int-col-4 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title" style={{ marginBottom: 12 }}>
                Tests
              </h4>
              <Table
                title="DatasetEvaluation"
                columns={columns}
                data={tests}
                paginationPerPage={perPage}
                paginationTotalRows={testsTotal}
                onChangePage={onChangePage}
                progressPending={testsLoading}
                onRowClicked={openTest}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SimulationDatasetEvaluationDetail() {
  return (
    <ConfirmationProvider>
      <SimulationDatasetApiProvider>
        <SimulationDatasetEvaluationDetailContent />
      </SimulationDatasetApiProvider>
    </ConfirmationProvider>
  );
}

export default SimulationDatasetEvaluationDetail;
